// MIT License - Copyright (c) 2026 Doug McNall
//
// Cookie-based JWT auth context for the shop app.
//
// Tokens live in HttpOnly cookies set by the gateway (ddhs_shop_access /
// ddhs_shop_refresh). The non-HttpOnly ddhs_shop_csrf cookie is readable
// and echoed in X-CSRF-Token on state-changing requests.
//
// Authentication state is derived from /auth/ping calls — if the gateway
// returns 200 we have a valid session, 401 means logged out. On mount we
// ping once to detect an existing session.
import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);
const apiUrl = process.env.REACT_APP_API_URL;

const csrfToken = () => {
  const m = document.cookie.match(/(?:^|;\s*)ddhs_shop_csrf=([^;]+)/);
  return m ? m[1] : '';
};

// Probe the gateway for a valid session.
const pingSession = async () => {
  const res = await fetch(`${apiUrl}/auth/ping`, { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`ping failed: ${res.status}`);
  const data = await res.json();
  return {
    email: data.email,
    id: data.id,
    first_name: data.first_name,
    last_name: data.last_name,
    attributes: { email: data.email, sub: data.id },
  };
};

// One-shot fetch helper for auth bootstrap calls (login/register/forgot).
// Sends/receives cookies but omits the CSRF header — these endpoints are
// CSRF-exempt server-side because they bootstrap the session.
const authPost = async (path, body) => {
  const res = await fetch(`${apiUrl}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || err.message || `Request failed (${res.status})`);
  }
  return res.json();
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: detect an existing session via /auth/ping. If the cookie
  // is present and valid the gateway returns 200 with profile fields;
  // otherwise we sit in the logged-out state.
  useEffect(() => {
    (async () => {
      try {
        const u = await pingSession();
        setUser(u);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      await authPost('/auth/login', { email, password });
      const u = await pingSession();
      setUser(u);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      await authPost('/auth/register', {
        email,
        password,
        first_name: name,
        last_name: '',
      });
      const u = await pingSession();
      setUser(u);
      return { success: true, userId: u && u.id };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const confirmRegistration = async () => ({ success: true });

  const logout = async () => {
    try {
      const csrf = csrfToken();
      await fetch(`${apiUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf && { 'X-CSRF-Token': csrf }),
        },
      });
    } catch { /* ignore — server-side revocation is best-effort */ }
    setUser(null);
  };

  const forgotPassword = async (email) => {
    try {
      await authPost('/auth/forgot-password', { email });
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const confirmForgotPassword = async () => ({ success: true });
  const changePassword = async () => ({ success: true });

  return (
    <AuthContext.Provider value={{
      user, loading, isAuthenticated: !!user,
      login, register, confirmRegistration, logout,
      forgotPassword, confirmForgotPassword, changePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
